import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Page,
  Card,
  BlockStack,
  InlineStack,
  TextField,
  Button,
  DataTable,
  Text,
  Badge,
  Box,
  SkeletonBodyText,
  EmptyState,
} from '@shopify/polaris';
import reportsApi from '@/services/reports';

const today = () => new Date().toISOString().split('T')[0];
const daysAgo = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split('T')[0];
};

const statusTone: Record<string, 'success' | 'info' | 'warning' | 'critical' | 'attention' | undefined> = {
  ready: 'success',
  in_use: 'info',
  qc_hold: 'warning',
  repair: 'critical',
  retired: 'attention',
};

export default function SerialReport() {
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState(daysAgo(30));
  const [endDate, setEndDate] = useState(today());

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['report', 'serial', startDate, endDate],
    queryFn: () => reportsApi.getSerial({ startDate, endDate }),
  });

  const [exporting, setExporting] = useState(false);
  const handleExport = async () => {
    setExporting(true);
    try {
      await reportsApi.downloadCsv('/reports/serial', { startDate, endDate });
    } finally {
      setExporting(false);
    }
  };

  const rows = (data?.data || []).map((a: any) => [
    a.serialNumber,
    a.productSku,
    a.productName || '\u2014',
    <Badge key={a.serialNumber} tone={statusTone[a.currentStatus]}>{a.currentStatus}</Badge>,
    String(a.totalTransactions),
    String(a.transactionsInPeriod),
    a.registered ? new Date(a.registered).toLocaleDateString() : '\u2014',
  ]);

  return (
    <Page
      title="Serial Number Report"
      backAction={{ content: 'Reports', onAction: () => navigate('/reports') }}
    >
      <Card>
        <BlockStack gap="400">
          <InlineStack gap="400" blockAlign="end">
            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={setStartDate}
              autoComplete="off"
            />
            <TextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={setEndDate}
              autoComplete="off"
            />
            <Box paddingBlockStart="600">
              <InlineStack gap="200">
                <Button onClick={() => refetch()}>Refresh</Button>
                <Button onClick={handleExport} loading={exporting}>
                  Export CSV
                </Button>
              </InlineStack>
            </Box>
          </InlineStack>

          {isLoading ? (
            <SkeletonBodyText lines={8} />
          ) : isError ? (
            <EmptyState heading="Failed to load report" image="">
              <p>There was an error loading the Serial Number report. Try refreshing.</p>
            </EmptyState>
          ) : !data?.data?.length ? (
            <EmptyState heading="No data" image="">
              <p>No asset data found.</p>
            </EmptyState>
          ) : (
            <BlockStack gap="400">
              <Text as="p" variant="bodyMd" tone="subdued">
                {data.data.length} assets registered
              </Text>
              <DataTable
                columnContentTypes={['text', 'text', 'text', 'text', 'numeric', 'numeric', 'text']}
                headings={['Serial #', 'SKU', 'Product', 'Status', 'Total Txns', 'Period Txns', 'Registered']}
                rows={rows}
                footerContent={rows.length === 0 ? 'No records found' : undefined}
              />
            </BlockStack>
          )}
        </BlockStack>
      </Card>
    </Page>
  );
}
